import { Component, inject, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ConnectionService } from "@services/connection-service";
import { LocalStorageService } from "@services/local-storage-service";
import { EventLoggerService } from "@services/event-logger-service";
import { AppWebBridgeService } from "@services/app-web-bridge-service";
import { BroadcastService } from "@services/broadcast-service";
import { AppConfig } from "../../app.config";
import { CurrentComponentService } from "@services/current-component";
import { ExperimentService } from "@services/experiment/experiment.service";
import { UserService } from "@services/user";
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { ONBOARDING_PROFILE_ROUTES } from "../onboarding-profile/onboarding-profile.constants";

/** Two layouts off one route, plus an undecided state so neither flashes while flags resolve. */
export enum ConcernPageView {
  RESOLVING = 'RESOLVING',
  SPLIT = 'SPLIT',
  CONTROL = 'CONTROL',
}

@Component({
  selector: "onboarding-concern",
  templateUrl: "./onboarding-concern.html",
  standalone: false,
})
export class OnboardingConcernComponent implements OnDestroy, OnInit {
  public readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  private readonly localStorageService: LocalStorageService =
    inject(LocalStorageService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  public readonly appConfig: AppConfig = inject(AppConfig);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly appWebBridge: AppWebBridgeService =
    inject(AppWebBridgeService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly userService: UserService = inject(UserService);

  user: any;

  protected readonly ConcernPageView: typeof ConcernPageView = ConcernPageView;
  protected readonly view: WritableSignal<ConcernPageView> = signal(ConcernPageView.RESOLVING);

  protected readonly firstName: WritableSignal<string> = signal('');
  protected readonly faceIconUrl: WritableSignal<string> = signal('');
  protected readonly hairIconUrl: WritableSignal<string> = signal('');

  get isDravidianScriptUser(): boolean {
    return this.userService.dravidianScripts.includes(this.user?.get('languagePreference'));
  }

  /** Male users lead with the hair card, mirroring `<regimen-concern-selection>`. */
  get showHairFirst(): boolean {
    return this.user?.get('Gender') === this.appConfig.Shared.Gender.MALE;
  }

  userLanguagePreference: string;
  selectedConcernClass: string;
  isOnboardingTakePhotoExpEnabled: boolean;
  startTimer: number;
  experiments: any[] = [];

  async ngOnInit(): Promise<void> {
    this.startTimer = new Date().getTime();
    this.user = this.conn.getActingUser();
    this.userLanguagePreference =
      this.user?.get("languagePreference") ||
      this.appConfig.Shared.Languages.EN;

    await this.experimentService.experimentsLoaded;
    this.view.set(this.experimentService.isUserProfileInputBreakdownEnabled()
      ? ConcernPageView.SPLIT
      : ConcernPageView.CONTROL);
    this.firstName.set(this.user?.get('PatientName')?.split(' ')?.[0] || '');
    this.setConcernIconUrls();

    this.experiments = await this.experimentService.fetchActiveUserExperiment();

    this.currentComponent.set(this);

    this.selectedConcernClass =
      this.localStorageService.getValue("Onboarding/PrivateMainConcernClass") ||
      this.activatedRoute.snapshot.queryParams.class ||
      this.appConfig.Shared.Regimen.Class.FACE;

    const redirectUrl: string =
      this.localStorageService.getValue("CureSkin/redirectUrl") || "";

    if (redirectUrl) {
      const classPreference: any =
        this.conn.findRelativePathAndQueryString(redirectUrl)[1];
      if (classPreference.class) {
        this.localStorageService.delete("CureSkin/redirectUrl");
        this.selectedConcernClass = classPreference.class;
      }
    }

    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent("onboarding_concern_page_opening_time", {
      timeInMilliSec: time,
      userId: this.user.id,
    });
    this.eventLogger.cleverTapEvent(
      "pageOpen",
      JSON.stringify({ pageName: "onboarding-concern-selection" }),
      PRIMARY_CHANNELS
    );
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: "pageOpenonboardingConcernSelection" },
      firebase: { name: "pageOpenonboardingConcernSelection" },
    });
  }

  async updateConcern(selectedConcernClass: any): Promise<void> {
    this.selectedConcernClass = selectedConcernClass;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: this.selectedConcernClass }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    if (!this.user) {
      this.broadcast.broadcast("NOTIFY", {
        message: "User not found. Kindly login.",
      });
      return;
    }
    try {
      this.localStorageService.setValue(
        "Onboarding/PrivateMainConcernClass",
        this.selectedConcernClass
      );
      // Makes main-concern's back return here rather than home; cleared once the user proceeds past it.
      this.localStorageService.setValue("onboardingFlow", true);
      const queryParams: Record<string, string> = {
        class: this.selectedConcernClass,
      };
      if (this.activatedRoute.snapshot.queryParams.redirectOnBack) {
        queryParams.redirectOnBack =
          this.activatedRoute.snapshot.queryParams.redirectOnBack;
      }
      const time = new Date().getTime() - this.startTimer;
      this.eventLogger.trackEvent("onboarding_concern_time_spent", {
        timeInMilliSec: time,
        userId: this.user.id,
      });
      await this.router.navigate(["/mainConcern"], { queryParams });
    } catch (error) {
      this.broadcast.broadcast("NOTIFY", { message: error.toString() });
    }
  }

  /** Same face/hair artwork `<regimen-concern-selection>` uses, so both variants stay consistent. */
  private setConcernIconUrls(): void {
    const isMale = this.user?.get('Gender') === this.appConfig.Shared.Gender.MALE;
    const genderSlug = isMale ? 'male' : 'female';
    this.faceIconUrl.set(`https://cdn.cureskin.com/app/img/rebranded-${genderSlug}-face.svg`);
    this.hairIconUrl.set(`https://cdn.cureskin.com/app/img/rebranded-${genderSlug}-hair.svg`);
  }

  /** Returns to the last profile page the user actually saw. Only reachable in the split flow. */
  protected async handleBack(): Promise<void> {
    const previousPage = this.experimentService.shouldSkipAgePage()
      ? ONBOARDING_PROFILE_ROUTES.NAME
      : ONBOARDING_PROFILE_ROUTES.AGE;
    await this.router.navigate([previousPage]);
  }

  /** Swallowed on the control page, which has no previous step; mirrors the chevron in the split flow. */
  async handleBackPress(): Promise<boolean> {
    if (this.experimentService.isUserProfileInputBreakdownEnabled()) await this.handleBack();
    return true;
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
  }
}
